import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Music, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { MusicCard } from '@/components/cards';
import { SongActionsMenu } from '@/components/dialogs';
import { LoadingState, EmptyState } from '@/components/states';
import { useAuth } from '@/context/AuthContext';
import { UnauthorizedState } from '@/components/states';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export default function Library() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [styleId, setStyleId] = useState('all');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/library', {
        params: { q: q || undefined, style_id: styleId === 'all' ? undefined : styleId, sort },
      });
      setSongs(data);
    } finally {
      setLoading(false);
    }
  }, [q, styleId, sort]);

  useEffect(() => { if (session) { api.get('/styles').then(({ data }) => setStyles(data)); } }, [session]);
  useEffect(() => { if (session) load(); }, [session, load]);

  if (!session) return <UnauthorizedState />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Bibliothèque</h1>
          <p className="mt-1 text-muted-foreground">Tes créations musicales.</p>
        </div>
        <button onClick={() => navigate('/create')} className="hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow sm:flex" data-testid="library-create-button">
          <Plus className="h-4 w-4" /> Nouveau
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..."
                 data-testid="library-search"
                 className="w-full rounded-full border border-border bg-surface-input py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
        </div>
        <Select value={styleId} onValueChange={setStyleId}>
          <SelectTrigger className="w-full border-border bg-surface-input sm:w-48" data-testid="library-style-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="glass">
            <SelectItem value="all">Tous les styles</SelectItem>
            {styles.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full border-border bg-surface-input sm:w-40" data-testid="library-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="glass">
            <SelectItem value="newest">Plus récents</SelectItem>
            <SelectItem value="title">Titre (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingState /> : songs.length === 0 ? (
        <EmptyState icon={Music} title="Ta bibliothèque est vide"
                    description="Crée ta première musique pour la retrouver ici."
                    action={<button onClick={() => navigate('/create')} className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-black btn-glow" data-testid="empty-create-button">Composer une chanson</button>} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {songs.map((s) => (
            <MusicCard key={s.id} song={s} queue={songs.map(toTrack)}
                       actions={<SongActionsMenu song={s} onDeleted={(id) => setSongs((p) => p.filter((x) => x.id !== id))} />} />
          ))}
        </div>
      )}
    </div>
  );
}

function toTrack(s) {
  return {
    song_id: s.id, title: s.title, slug: s.slug, cover_url: s.cover_url,
    audio_url: s.audio_url, style_text: s.style_text, duration_seconds: s.duration_seconds,
  };
}
